import React from "react";
import { Link } from "react-router-dom";
import { Controller } from "react-hook-form";
import { Col, Form, FormControl } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { FaEye, FaEyeSlash, FaRegCalendarAlt, FaMinus } from "react-icons/fa";
import { buildValidationRules } from "./studentSignup3DataHelpers";
import PropTypes from "prop-types";
import { getSubdomainPath, normalizeMonthYear } from "../../../utils/helpers";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const toggleExperienceValue = (currentValue, id) => {
  return currentValue.includes(id)
    ? currentValue.filter((item) => item !== id)
    : [...currentValue, id];
};

const handleExperienceChange = ({
  id,
  currentValue,
  field,
  setExperience,
  clearErrors,
  reg_key,
}) => {
  const newValues = toggleExperienceValue(currentValue, id);

  field.onChange(newValues);
  setExperience(newValues);

  if (newValues.length > 0) {
    clearErrors(reg_key);
  }
};

const renderLinkedInField = (field, deps) => {
  const { control, errors } = deps;
  const { placeholder, validation, is_required, status } = field;
  const subDomainPath = getSubdomainPath();
  return status ? (
    <Col md={12} className="mb-3">
      <div className="form-group position-relative" style={{ marginBottom: "0px" }}>
        <Form.Label>{field.label}</Form.Label>
        <div className="form-label overflow-hidden ">
          <span>
            <i className="fab fa-linkedin-in"></i>
          </span>

          <Controller
            name="linkedin"
            control={control}
            defaultValue=""
            rules={{
              ...buildValidationRules(validation, is_required),
            }}
            render={({ field }) => (
              <Form.Control
                type="text"
                placeholder={placeholder}
                className="form-control"
                {...field}
              />
            )}
          />
        </div>
        {(subDomainPath !== 'siddev' && subDomainPath !== 'siduat' && subDomainPath !== 'sid') &&
          `If you are registering as both a mentor and a mentee, please add a
          single or double slash ("/" or "//") at the end of your LinkedIn link.`
        }
      </div>

      {errors?.linkedin && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors.linkedin.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderProgramStudyOtherField = (field, deps) => {
  const { register, errors, watch } = deps;
  const { reg_key, field_type, placeholder, validation, status, is_required } = field;
  const isNameField = ["first_name", "last_name"].includes(reg_key);

  let columnClass = "";
  if (reg_key === "first_name") {
    columnClass = "pe-sm-2";
  } else if (reg_key === "last_name") {
    columnClass = "ps-sm-2";
  }

  return status && watch("program_study_id") === "0" ? (
    <Col
      md={isNameField ? 6 : 12}
      sm={isNameField ? 6 : 12}
      className={`mb-3 ${columnClass}`}
    >
      <Form.Group controlId="formBasicEmail">
        <Form.Label>{field.label}</Form.Label>
        <Form.Control
          type={field_type}
          placeholder={placeholder}
          name={reg_key}
          {...register(reg_key, {
            ...buildValidationRules(validation, is_required),
            validate: {
              whiteSpace: (value) =>
                !value ||
                  (typeof value === "string" && value.trim().length > 0)
                  ? true
                  : "White spaces not allowed.",
            },
          })}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderGenericTextField = (field, deps) => {
  const { register, errors } = deps;
  const { reg_key, field_type, label, placeholder, validation, status, is_required } = field;
  const isNameField = ["first_name", "last_name"].includes(reg_key);

  let columnClass = "";
  if (reg_key === "first_name") {
    columnClass = "pe-sm-2";
  } else if (reg_key === "last_name") {
    columnClass = "ps-sm-2";
  }

  return status ? (
    <Col
      md={isNameField ? 6 : 12}
      sm={isNameField ? 6 : 12}
      className={`mb-3 ${columnClass}`}
    >
      {(reg_key === "restrictions_boundaries" ||
        reg_key === "communication_tools" ||
        reg_key === "frequency_communication" ||
        reg_key === "topics") && (
          <label className="form-label">{label}</label>
        )}

      <Form.Control
        type={field_type}
        placeholder={placeholder}
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validation, is_required),
          validate: {
            whiteSpace: (value) =>
              !value ||
                (typeof value === "string" && value.trim().length > 0)
                ? true
                : "White spaces not allowed.",
          },
        })}
      />
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

export const renderTextField = (field, deps) => {
  const { reg_key } = field;

  if (reg_key === "linkedin") {
    return renderLinkedInField(field, deps);
  }

  if (reg_key === "program_study_other") {
    return renderProgramStudyOtherField(field, deps);
  }

  if (reg_key === "past_board_roles") {
    return renderPastBoardRolesField(field, deps);
  }

  return renderGenericTextField(field, deps);
};

export const renderEmailField = (field, deps) => {
  const { register, errors, email, setEmail, readOnly, setValue } = deps;
  const { reg_key, field_type, placeholder, validation, is_required } = field;
  return (
    <Col md={12} sm={12} className="mb-3">
      <Form.Group controlId="formBasicEmail">
        <Form.Label>{field.label}</Form.Label>
        <FormControl
          type={field_type}
          placeholder={placeholder}
          name={reg_key}
          value={email}
          readOnly={readOnly}
          {...register(reg_key, {
            ...buildValidationRules(validation, is_required),
          })}
          onChange={(e) => {
            setValue("email", e.target.value);
            setEmail(e?.target?.value);
          }}
        />
        {errors?.email && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.email?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  );
};

export const renderPasswordField = (field, deps) => {
  const { register, errors, showPassword, togglePasswordVisibility } = deps;
  const { reg_key, placeholder, validation, is_required } = field;

  return (
    <Col md={12} className="mb-3">
      <Form.Group controlId="formBasicPassword">
        <Form.Label>{field.label}</Form.Label>
        <div style={{ position: "relative" }}>
          <FormControl
            type={showPassword ? "text" : "password"}
            placeholder={placeholder}
            name={reg_key}
            {...register(reg_key, {
              ...buildValidationRules(validation, is_required),
            })}
          />
          <button
            type="button"
            className="password-toggle-icon"
            onClick={togglePasswordVisibility}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                togglePasswordVisibility();
              }
            }}
            style={{
              cursor: "pointer",
              position: "absolute",
              right: "13px",
              top: "47%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              padding: 0,
            }}
          >
            {showPassword ? <FaEye /> : <FaEyeSlash />}
          </button>
        </div>
        {errors?.password && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.password?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  );
};

// SingleSelect field renderers
const renderRoleSelect = (field, deps) => {
  const { register, errors, handleChange, roleId, email_verify_string } = deps;
  const { reg_key, placeholder, validation, is_required } = field;

  return (
    <Col md="12" className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <select
        name={reg_key}
        disabled={!!email_verify_string}
        className={"form-control"}
        value={roleId}
        {...register(reg_key, {
          ...buildValidationRules(validation, is_required),
        })}
        onChange={(e) => {
          handleChange(e.target.value);
        }}
      >
        <option value="">{placeholder}</option>
        <option value="Mentor">Mentor</option>
        <option value="Mentees">Mentee</option>
      </select>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  );
};

const renderProgramSelect = (field, deps) => {
  const { control, programmes, setValue } = deps;
  const { reg_key, placeholder, status } = field;
  const defaultProgramme = programmes?.[0]?.id ?? "";

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Controller
        name={reg_key}
        control={control}
        rules={{}}
        defaultValue={defaultProgramme}
        render={({ field: controllerField }) => {
          const currentValue = controllerField.value !== undefined && controllerField.value !== ""
            ? controllerField.value
            : defaultProgramme;

          // Sync into RHF state so the value appears in the submit payload.
          // This is needed because disabled fields are excluded from native form
          // submission, and Controller's defaultValue alone is not enough when
          // superUserParams doesn't pre-populate this key.
          // Use both conditions to ensure proper sync when programmes data loads
          if (programmes && programmes.length > 0 && (!controllerField.value || controllerField.value === "")) {
            // Immediately sync the default value into form state
            setValue(reg_key, defaultProgramme, { shouldValidate: false });
          } else if (currentValue !== "" && controllerField.value !== currentValue) {
            // Also handle case where value needs updating
            setTimeout(() => setValue(reg_key, currentValue, { shouldValidate: false }), 0);
          }

          return (
            <select
              value={currentValue}
              disabled
              className="form-control"
            >
              <option value="">{placeholder}</option>
              {programmes?.map((value) => (
                <option key={value.id} value={value.id}>
                  {value.program}
                </option>
              ))}
            </select>
          );
        }}
      />
    </Col>
  ) : null;
};

const renderProgramStudySelect = (field, deps) => {
  const { register, errors, setValue, trigger, clearErrors, programStudyId, handleProgrammesChange } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <select
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validation, is_required),
        })}
        className={`form-control ${errors?.[reg_key] ? "is-invalid" : ""}`}
        onChange={(e) => {
          const value = e.target.value;
          handleProgrammesChange(value);
          setValue(reg_key, value);
          if (value) {
            clearErrors(reg_key);
          } else {
            trigger(reg_key);
          }
        }}
      >
        <option value="">{placeholder}</option>
        {programStudyId?.map((value) => (
          <option key={value.id} value={value.id}>
            {value.title}
          </option>
        ))}
        <option value={"0"}>Others</option>
      </select>

      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
            display: "block",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderUniversityPartnerSelect = (field, deps) => {
  const { register, errors, trigger, clearErrors, university_partners_list } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group controlId="formBasicPassword">
        <Form.Label>{field.label}</Form.Label>
        <Form.Control
          as="select"
          name={reg_key}
          {...register(reg_key, {
            ...buildValidationRules(validation, is_required),
          })}
          className={"form-control"}
          onChange={(e) => {
            clearErrors(reg_key);
            if (!e.target.value && is_required) {
              trigger(reg_key);
            }
          }}
        >
          <option value="">{placeholder}</option>
          {university_partners_list?.map((item) => (
            <option value={item.id} key={item.id}>
              {item.title}
            </option>
          ))}
        </Form.Control>

        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderYearSelect = (field, deps) => {
  const { register, errors, years } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group controlId="formCurrentCourse">
        <Form.Label>{field.label}</Form.Label>
        <Form.Control
          as="select"
          name={reg_key}
          {...register(reg_key, {
            ...buildValidationRules(validation, is_required),
          })}
          className={"form-control"}
        >
          <option value="">{placeholder}</option>
          {years?.map((year) => (
            <option value={year} key={year}>
              {year}
            </option>
          ))}
        </Form.Control>
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderExperienceSelect = (field, deps) => {
  const { register, errors, clearErrors, userExperience, setExperience } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;

  return status ? (
    <Col md="12" className="mb-3">
      <Form.Group controlId="formBasicPassword">
        <Form.Label>{field.label}</Form.Label>
        <Form.Control
          as="select"
          name={reg_key}
          id={`experience-${reg_key}`}
          {...register(reg_key, {
            ...buildValidationRules(validation, is_required, reg_key),
          })}
          className={"form-control mb-3"}
          onChange={(e) => {
            setExperience([e.target.value]);
            clearErrors(reg_key);
          }}
        >
          <option value="">{placeholder}</option>
          {userExperience?.map((val) => (
            <option key={val?.id} value={val?.id}>
              {val?.title}
            </option>
          ))}
        </Form.Control>
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderCountrySelect = (field, deps) => {
  const { register, errors, trigger, clearErrors, country, setValue, watch } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;

  const currentCountry = watch(reg_key) || "";

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        value={currentCountry}
        className="mb-1"
        name={reg_key}
        onChange={(e) => {
          const selectedCountry = e.target.value;
          setValue(reg_key, selectedCountry);
          clearErrors(reg_key);
          if (!selectedCountry && is_required) {
            trigger(reg_key);
          }
        }}
        {...register(reg_key, {
          ...buildValidationRules(validation, is_required),
        })}
      >
        <option value="">{placeholder}</option>
        {country?.map((item) => (
          <option value={item.label} key={item.label}>
            {item.label}
          </option>
        ))}
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderLanguageSelect = (field, deps) => {
  const { control, errors, setValue, trigger, clearErrors, languageOptions, customStyles } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group controlId="formBasicLanguage">
        <Form.Label>{field.label}</Form.Label>
        <Controller
          name="language"
          control={control}
          rules={{
            ...buildValidationRules(validation, is_required, reg_key),
          }}
          render={({ field }) => (
            <Select
              {...field}
              isMulti
              options={languageOptions}
              placeholder={placeholder}
              className="basic-multi-select language_select"
              classNamePrefix="select"
              inputId={`language-${reg_key}`}
              onChange={(selectedOptions) => {
                const selectedValues = selectedOptions
                  ? selectedOptions?.map((opt) => opt.value)
                  : [];
                field.onChange(selectedValues);
                setValue(
                  "language",
                  selectedValues?.length > 0 ? selectedValues : ""
                );
                clearErrors(reg_key);
                if (selectedValues.length === 0 && is_required) {
                  trigger(reg_key);
                }
              }}
              value={languageOptions?.filter((option) =>
                field.value?.includes(option?.value)
              )}
              styles={customStyles}
            />
          )}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderIndustrySelect = (field, deps) => {
  const { register, errors, setIndustryData, industryData, industry_list } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        defaultValue={industryData}
        className="mb-1"
        name={reg_key}
        onChange={(e) => {
          setIndustryData(e?.target?.value);
        }}
        {...register(reg_key, {
          ...buildValidationRules(validation, is_required),
        })}
      >
        <option value="">{placeholder}</option>
        {industry_list?.map((item) => (
          <option value={item.id} key={item.id}>
            {item.title}
          </option>
        ))}
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderMenteeCountSelect = (field, deps) => {
  const { register, errors } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  return status ? (
    <Col md="12" className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validation, is_required),
        })}
        className={"form-control"}
      >
        <option value="">{placeholder}</option>
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
        <option value="5">5</option>
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderProgramInterestSelect = (field, deps) => {
  const { register, errors, particularProgram } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  return status ? (
    <Col md="12" className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        name={reg_key}
        id={`program-interest-${reg_key}`}
        {...register(reg_key, {
          ...buildValidationRules(validation, is_required),
        })}
        className={"form-control"}
      >
        <option value="">{placeholder}</option>
        {particularProgram?.map((res, i) => {
          return (
            <option key={res?.id} value={res?.id}>
              {`${res?.title}`}
            </option>
          );
        })}
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

// Board-related field renderers
const renderCoursesCompletedCustomSelect = (field, deps) => {
  const { control, errors, coursesCompleted, customStyles, setValue } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  const options = coursesCompleted?.map(item => ({
    value: item.id,
    label: item.title
  })) || [];

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group>
        <Form.Label>{field.label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          defaultValue={[]} // Ensure default value is an array
          rules={{
            ...buildValidationRules(validationRule, is_required),
          }}
          render={({ field }) => {
            // Normalize field value to ensure consistent comparison
            const fieldValue = Array.isArray(field.value) ? field.value : [];
            const normalizedFieldValue = new Set(
              fieldValue.map((val) => Number.parseInt(val))
            );

            return (
              <Select
                {...field}
                isMulti
                options={options}
                placeholder={placeholder}
                className="basic-multi-select"
                classNamePrefix="select"
                onChange={(selectedOptions) => {
                  const selectedValues = selectedOptions
                    ? selectedOptions.map((opt) => Number.parseInt(opt.value))
                    : [];
                  field.onChange(selectedValues);
                  setValue(reg_key, selectedValues);
                }}
                value={options.filter((option) =>
                  normalizedFieldValue.has(Number.parseInt(option.value))
                )}
                styles={customStyles}
              />
            );
          }}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderBoardEntityTypeSelect = (field, deps) => {
  const { control, errors, setValue, boardEntityTypeExperience, boardExperienceMentor, customStyles } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  // Use different data source based on field type
  const dataSource = reg_key === "board_entity_type_2" ? boardExperienceMentor : boardEntityTypeExperience;
  const options = dataSource?.map(item => ({
    value: item.id,
    label: item.title
  })) || [];

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group>
        <Form.Label>{field.label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          defaultValue={[]} // Ensure default value is an array
          rules={{
            ...buildValidationRules(validationRule, is_required),
          }}
          render={({ field }) => {
            // Normalize field value to ensure consistent comparison
            const fieldValue = Array.isArray(field.value) ? field.value : [];
            const normalizedFieldValue = new Set(
              fieldValue.map((val) => Number.parseInt(val))
            );

            return (
              <Select
                {...field}
                isMulti
                options={options}
                placeholder={placeholder}
                className="basic-multi-select"
                classNamePrefix="select"
                onChange={(selectedOptions) => {
                  const selectedValues = selectedOptions
                    ? selectedOptions.map((opt) => Number.parseInt(opt.value))
                    : [];
                  field.onChange(selectedValues);
                  setValue(
                    reg_key,
                    selectedValues?.length > 0 ? selectedValues : ""
                  );
                }}
                value={options.filter((option) =>
                  normalizedFieldValue.has(Number.parseInt(option.value))
                )}
                styles={customStyles}
              />
            );
          }}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderYearsOfBoardExperienceSelect = (field, deps) => {
  const { register, errors, watch, boardExperience } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;
  const currentValue = watch(reg_key) || "";

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        name={reg_key}
        value={currentValue}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
        className="form-control"
      >
        <option value="">{placeholder}</option>
        {boardExperience?.map((item) => (
          <option key={item.id} value={item.id}>
            {item.title}
          </option>
        ))}
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderAccreditedDirectorSelect = (field, deps) => {
  const { control, errors, accreditedDirector, customStyles, setValue } = deps;
  const { reg_key, label, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  const options = accreditedDirector?.map(item => ({
    value: item.id,
    label: item.title
  })) || [];

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group>
        <Form.Label>{label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          defaultValue={null}
          rules={{
            ...buildValidationRules(validationRule, is_required),
          }}
          render={({ field }) => {
            const selectedValue = options.find(
              (option) => Number.parseInt(option.value) === Number.parseInt(field.value)
            );
            return (
              <Select
                {...field}
                options={options}
                placeholder={placeholder}
                className="basic-multi-select"
                classNamePrefix="select"
                onChange={(selectedOption) => {
                  const value = selectedOption
                    ? Number.parseInt(selectedOption.value)
                    : null;

                  field.onChange(value);
                  setValue(reg_key, value);
                }}
                value={selectedValue || null}
                styles={customStyles}
              />
            );
          }}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderBoardExperienceLevelSelect = (field, deps) => {
  const { control, errors, boardExperienceLevel, customStyles, setValue } = deps;
  const { reg_key, label, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  const options = boardExperienceLevel?.map(item => ({
    value: item.id,
    label: item.title
  })) || [];

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group>
        <Form.Label>{label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          defaultValue={[]} // Ensure default value is an array
          rules={{
            ...buildValidationRules(validationRule, is_required),
          }}
          render={({ field }) => {
            // Normalize field value to ensure consistent comparison
            const fieldValue = Array.isArray(field.value) ? field.value : [];
            const normalizedFieldValue = new Set(
              fieldValue.map((val) => Number.parseInt(val))
            );

            return (
              <Select
                {...field}
                isMulti
                options={options}
                placeholder={placeholder}
                className="basic-multi-select"
                classNamePrefix="select"
                onChange={(selectedOptions) => {
                  const selectedValues = selectedOptions
                    ? selectedOptions.map((opt) => Number.parseInt(opt.value))
                    : [];
                  field.onChange(selectedValues);
                  setValue(
                    reg_key,
                    selectedValues?.length > 0 ? selectedValues : ""
                  );
                }}
                value={options.filter((option) =>
                  normalizedFieldValue.has(Number.parseInt(option.value))
                )}
                styles={customStyles}
              />
            );
          }}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderCoursesCompletedSelect = (field, deps) => {
  const { register, errors, coursesCompleted } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
        className="form-control"
      >
        <option value="">{placeholder}</option>
        {coursesCompleted?.map((item) => (
          <option key={item.id} value={item.id}>
            {item.title}
          </option>
        ))}
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

// Board multi-select fields (similar to language field)
const renderBoardEntityTypeMultiSelect = (field, deps) => {
  const { control, errors, setValue, trigger, clearErrors, boardEntityTypeExperience, customStyles } = deps;
  const { reg_key, label, placeholder, validation, status, is_required } = field;

  const options = boardEntityTypeExperience?.map(item => ({
    value: item.id,
    label: item.title
  })) || [];

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group controlId={`board-entity-type-${reg_key}`}>
        <Form.Label htmlFor={`board-entity-type-${reg_key}`}>{label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          rules={{
            ...buildValidationRules(validation, is_required, reg_key),
          }}
          render={({ field }) => (
            <Select
              {...field}
              isMulti
              options={options}
              placeholder={placeholder}
              className="basic-multi-select board_entity_type_select"
              classNamePrefix="select"
              inputId={`board-entity-type-${reg_key}`}
              onChange={(selectedOptions) => {
                const selectedValues = selectedOptions
                  ? selectedOptions?.map((opt) => opt.value)
                  : [];
                field.onChange(selectedValues);
                setValue(
                  reg_key,
                  selectedValues?.length > 0 ? selectedValues : ""
                );
                clearErrors(reg_key);
                if (selectedValues.length === 0 && is_required) {
                  trigger(reg_key);
                }
              }}
              value={options?.filter((option) =>
                Array.isArray(field.value) && field.value?.includes(option?.value)
              )}
              styles={customStyles}
            />
          )}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderBoardEntityType2MultiSelect = (field, deps) => {
  const { control, errors, setValue, trigger, clearErrors, boardExperienceMentor, customStyles } = deps;
  const { reg_key, label, placeholder, validation, status, is_required } = field;

  const options = boardExperienceMentor?.map(item => ({
    value: item.id,
    label: item.title
  })) || [];

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group controlId={`board-entity-type-2-${reg_key}`}>
        <Form.Label htmlFor={`board-entity-type-2-${reg_key}`}>{label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          rules={{
            ...buildValidationRules(validation, is_required, reg_key),
          }}
          render={({ field }) => (
            <Select
              {...field}
              isMulti
              options={options}
              placeholder={placeholder}
              className="basic-multi-select board_entity_type_2_select"
              classNamePrefix="select"
              inputId={`board-entity-type-2-${reg_key}`}
              onChange={(selectedOptions) => {
                const selectedValues = selectedOptions
                  ? selectedOptions?.map((opt) => opt.value)
                  : [];
                field.onChange(selectedValues);
                setValue(
                  reg_key,
                  selectedValues?.length > 0 ? selectedValues : ""
                );
                clearErrors(reg_key);
                if (selectedValues.length === 0 && is_required) {
                  trigger(reg_key);
                }
              }}
              value={options?.filter((option) =>
                Array.isArray(field.value) && field.value?.includes(option?.value)
              )}
              styles={customStyles}
            />
          )}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderPastBoardRolesField = (field, deps) => {
  const { control, errors, setting } = deps;
  const { reg_key, label, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  let maxRoles = 6;

  if (setting?.board_roles_validation) {
    try {
      // Parse JSON string if needed
      const validationData = typeof setting.board_roles_validation === 'string'
        ? JSON.parse(setting.board_roles_validation)
        : setting.board_roles_validation;

      // Find min and max values - ignore the type field completely
      if (Array.isArray(validationData)) {
        // Find the min entry (ignore type)
        // Find the max entry (ignore type)
        const maxObj = validationData.find(v => v.key === 'max');
        if (maxObj?.value) {
          maxRoles = maxObj.value;
        }
      }
    } catch (error) {
      console.error('Error parsing board_roles_validation:', error);
    }
  }

  // Prioritize past_board_roles_count from settings if available
  if (
    setting?.past_board_roles_count !== undefined &&
    setting?.past_board_roles_count !== null &&
    setting?.past_board_roles_count !== ""
  ) {
    const parsedCount = parseInt(setting.past_board_roles_count, 10);
    if (!isNaN(parsedCount) && parsedCount > 0) {
      maxRoles = parsedCount;
    }
  } else if (maxRoles < 6) {
    maxRoles = 6;
  }

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group>
        <Form.Label>{label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          defaultValue={[]} // Ensure default value is an array
          rules={{
            ...buildValidationRules(validationRule, is_required),

            required: `Current / Past Board Roles minimum 1 and maximum ${maxRoles} roles are required.`,

            validate: (value) => {
              if (!value || value.length === 0) {
                return `Current / Past Board Roles minimum 1 and maximum ${maxRoles} roles are required.`;
              }

              if (value.length > maxRoles) {
                return `Current / Past Board Roles minimum 1 and maximum ${maxRoles} roles are required.`;
              }

              return true;
            }
          }}
          render={({ field }) => (
            <CreatableSelect
              {...field}
              isMulti
              options={[]}
              placeholder={placeholder}
              className="basic-multi-select"
              classNamePrefix="select"
              formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
              // Disable adding when max is reached
              onChange={(selectedOptions) => {
                // Only allow changes if not at max, or if removing items
                const selectedValues = selectedOptions
                  ? selectedOptions.map((opt) => opt.value || opt.label)
                  : [];

                // Prevent adding beyond max
                if (selectedValues.length <= maxRoles) {
                  field.onChange(selectedValues);
                }
              }}
              value={
                Array.isArray(field.value)
                  ? field.value.map(val => ({ value: val, label: val }))
                  : []
              }
              components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
                Menu: () => null, // Remove the dropdown menu completely
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '38px',
                  border: '1px solid #ced4da',
                  borderRadius: '0.375rem',

                  '&:hover': {
                    borderColor: '#ced4da'
                  }
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#e9ecef',
                  borderRadius: '0.25rem'
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: '#495057'
                }),
                placeholder: (base) => ({
                  ...base,
                  color: '#6c757d'
                })
              }}
            />
          )}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

export const renderSingleSelectField = (field, deps) => {
  const { reg_key } = field;

  switch (reg_key) {
    case "role_id":
      return renderRoleSelect(field, deps);
    case "programmes":
      return renderProgramSelect(field, deps);
    case "program_study_id":
      return renderProgramStudySelect(field, deps);
    case "university_partners_id":
      return renderUniversityPartnerSelect(field, deps);
    case "year_of_graduation":
      return renderYearSelect(field, deps);
    case "experience_levels":
      return renderExperienceSelect(field, deps);
    case "country":
      return renderCountrySelect(field, deps);
    case "language":
      return renderLanguageSelect(field, deps);
    case "industry_id":
      return renderIndustrySelect(field, deps);
    case "number_of_mantees":
      return renderMenteeCountSelect(field, deps);
    case "any_particular_program":
      return renderProgramInterestSelect(field, deps);
    // Board-related fields
    case "years_of_board_experience":
      return renderYearsOfBoardExperienceSelect(field, deps);
    case "accredited_director":
      return renderAccreditedDirectorSelect(field, deps);
    case "indicate_board_experience_level":
      return renderBoardExperienceLevelSelect(field, deps);
    case "course_completed":
      return renderCoursesCompletedSelect(field, deps);
    // Board multi-select fields (treated as SingleSelect but rendered as multi-select like language)
    case "board_entity_type":
      return renderBoardEntityTypeMultiSelect(field, deps);
    case "board_entity_type_2":
      return renderBoardEntityType2MultiSelect(field, deps);
    default:
      return null;
  }
};

// Radio field renderers
const renderEmployeeRadio = (field, deps) => {
  const { register, errors, employee, setOrganizationPlaceholder, setValue, clearErrors } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;

  const handleRadioChange = (val, e) => {
    if (setOrganizationPlaceholder) setOrganizationPlaceholder(e);
    if (setValue) {
      setValue(reg_key, val, { shouldValidate: true });
      if (val?.toLowerCase() === "yes") {
        setValue("employee_end_date", "");
        if (clearErrors) clearErrors("employee_end_date");
      }
    }
  };

  return status ? (
    <Col sm={12} className="mb-3">
      <div className="d-flex flex-column">
        <Form.Label>{field.label}</Form.Label>
        <div className="d-flex flex-column">
          <span className="d-flex">
            <span className="custom_radio ms-0">
              {placeholder?.split("|")?.[0]} &nbsp;
              <input
                type="radio"
                name={reg_key}
                checked={employee?.toLowerCase() === "yes" ? "checked" : ""}
                value={placeholder?.split("|")?.[0]}
                {...register(reg_key, {
                  ...buildValidationRules(validation, is_required),
                })}
                onChange={(e) => handleRadioChange(placeholder?.split("|")?.[0], e)}
              />
              <span className="radio_indicator">&nbsp;</span>
            </span>
            <span className="custom_radio">
              {placeholder?.split("|")?.[1]} &nbsp;{" "}
              <input
                type="radio"
                name={reg_key}
                checked={employee?.toLowerCase() === "no" ? "checked" : ""}
                value={placeholder?.split("|")?.[1]}
                {...register(reg_key, {
                  ...buildValidationRules(validation, is_required),
                })}
                onChange={(e) => handleRadioChange(placeholder?.split("|")?.[1], e)}
              />
              <span className="radio_indicator">&nbsp;</span>
            </span>
          </span>
          {errors?.[reg_key] && (
            <span
              role="alert"
              style={{
                color: "#dc3545",
                fontSize: "0.875em",
                margin: "4px 0px 8px",
              }}
            >
              {errors?.[reg_key]?.message}
            </span>
          )}
        </div>
      </div>
    </Col>
  ) : null;
};

const renderGenderRadio = (field, deps) => {
  const { register, errors, gender, handleGender } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  return status ? (
    <Col md={12} className="mb-3">
      <div className="d-flex flex-column">
        <div className="form-group" style={{ marginBottom: "0px" }}>
          <Form.Label>{field.label}</Form.Label>
        </div>
        <div className="form-group d-flex" style={{ marginBottom: "0px" }}>
          <span className="">
            <span className="custom_radio ms-0">
              {placeholder?.split("|")?.[0]} &nbsp;
              <input
                type="radio"
                name={reg_key}
                value={placeholder?.split("|")?.[0]}
                checked={gender?.toLowerCase() === "male" ? "checked" : ""}
                {...register(reg_key, {
                  ...buildValidationRules(validation, is_required),
                })}
                onClick={(e) => handleGender(e.target.value)}
              />
              <span className="radio_indicator">&nbsp;</span>
            </span>
            <span className="custom_radio">
              {placeholder?.split("|")?.[1]} &nbsp;
              <input
                type="radio"
                name={reg_key}
                value={placeholder?.split("|")?.[1]}
                checked={gender?.toLowerCase() === "female" ? "checked" : ""}
                {...register(reg_key, {
                  ...buildValidationRules(validation, is_required),
                })}
                onClick={(e) => handleGender(e.target.value)}
              />
              <span className="radio_indicator">&nbsp;</span>
            </span>
          </span>
        </div>
      </div>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

export const renderRadioField = (field, deps) => {
  const { reg_key } = field;

  if (reg_key === "employee") {
    return renderEmployeeRadio(field, deps);
  }

  if (reg_key === "gender") {
    return renderGenderRadio(field, deps);
  }

  return null;
};

// Checkbox field renderers
const renderSimpleCheckbox = (field, deps) => {
  const { register, errors } = deps;
  const { reg_key, label, validation, status, is_required } = field;
  return status ? (
    <Col md="12" className="mb-3">
      <div>
        <span className="custom_check me-2 ">
          {label}
          <input
            type="checkbox"
            name={reg_key}
            {...register(reg_key, {
              ...buildValidationRules(validation, is_required),
            })}
          />
          <span className="check_indicator">&nbsp;</span>
        </span>
      </div>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderMentalHealthCheckbox = (field, deps) => {
  const { register, errors, trigger, clearErrors, checkBox, setCheckBox } = deps;
  const { reg_key, label, validation, status, is_required } = field;
  return status ? (
    <Col md="12" className="mb-3">
      <label className="form-label" htmlFor={`mental-health-${reg_key}`}>Indicate your preference for below</label>
      <div>
        <span className="custom_check me-2 ">
          {label}
          <input
            type="checkbox"
            name={reg_key}
            value={checkBox}
            {...register(reg_key, {
              ...buildValidationRules(validation, is_required),
            })}
            onChange={(e) => {
              const isChecked = e.target.checked;
              setCheckBox(isChecked);

              if (isChecked) {
                clearErrors(reg_key);
              } else {
                trigger(reg_key);
              }
            }}
          />
          <span className="check_indicator">&nbsp;</span>
        </span>
      </div>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderCertifiedCoachCheckbox = (field, deps) => {
  const { register, errors, trigger, clearErrors, checkBox2, setCheckBox2 } = deps;
  const { reg_key, label, validation, status, is_required } = field;
  return status ? (
    <Col md="12" className="mb-3">
      <div>
        <span className="custom_check me-2 ">
          {label}
          <input
            type="checkbox"
            name={reg_key}
            value={checkBox2}
            {...register(reg_key, {
              ...buildValidationRules(validation, is_required),
            })}
            onChange={(e) => {
              const isChecked = e.target.checked;
              setCheckBox2(isChecked);

              if (isChecked) {
                clearErrors(reg_key);
              } else {
                trigger(reg_key);
              }
            }}
          />
          <span className="check_indicator">&nbsp;</span>
        </span>
      </div>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

// const renderTermsCheckbox = ({
//   reg_key,
//   label,
//   register,
//   validationRule,
//   is_required,
//   errors,
// }) => {
const renderTermsCheckbox = (field, deps) => {
  const {
    reg_key,
    label,
    validationRule,
    is_required,
  } = field;

  const { register, errors } = deps;
  return (
    <Col md={12} className="mb-3">
      <div className="form-group termsconditions mb-0 d-flex">

        <span className="custom_check m-0">
          <input
            type="checkbox"
            className="form-check-input"
            {...register(reg_key, {
              ...buildValidationRules(
                validationRule,
                is_required
              ),
            })}
          />

          <span className="check_indicator">
            &nbsp;
          </span>
        </span>

        <span
          dangerouslySetInnerHTML={{
            __html: label,
          }}
        />
      </div>

      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  );
};

// const renderPrivacyCheckbox = ({
//   reg_key,
//   label,
//   register,
//   validationRule,
//   is_required,
//   errors,
// }) => {
const renderPrivacyCheckbox = (field, deps) => {
  const {
    reg_key,
    label,
    validationRule,
    is_required,
  } = field;

  const { register, errors } = deps;
  return (
    <Col md={12} className="mb-3">
      <div className="form-group termsconditions mb-0 d-flex">

        <span className="custom_check m-0">
          <input
            type="checkbox"
            className="form-check-input"
            {...register(reg_key, {
              ...buildValidationRules(
                validationRule,
                is_required
              ),
            })}
          />

          <span className="check_indicator">
            &nbsp;
          </span>
        </span>

        <span
          dangerouslySetInnerHTML={{
            __html: label,
          }}
        />
      </div>

      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  );
};

const renderConsentCheckbox = (field, deps) => {
  const { register, errors, watch, setValue, clearErrors, setIsConsent } = deps;
  const { reg_key, label, validation, status, is_required } = field;
  const consentValue = watch(reg_key);

  return status ? (
    <Col md={12} className="mb-3">
      <div className="form-group mb-0">
        <span className="custom_check">
          {label}
          <input
            type="checkbox"
            name={reg_key}
            checked={consentValue || false}
            className={`form-check-input`}
            onChange={(e) => {
              setIsConsent(e?.target?.checked);
              setValue(reg_key, e.target.checked);
              clearErrors(reg_key);
            }}
            {...register(reg_key, {
              ...buildValidationRules(validation, is_required, reg_key),
            })}
          />
          <span className="check_indicator">&nbsp;</span>
        </span>
      </div>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderExperienceLevelsCheckbox = (field, deps) => {
  const { control, errors, clearErrors, userExperience, setExperience } = deps;
  const { reg_key, label, validation, status, is_required } = field;

  if (!status) return null;

  return (
    <Col md="12" className="mb-3">
      <label
        className="form-label d-block"
        htmlFor={`experience-levels-${reg_key}`}
      >
        {label}
      </label>

      <Controller
        name={reg_key}
        control={control}
        rules={{
          ...buildValidationRules(validation, is_required, reg_key),
        }}
        render={({ field }) => {
          const currentValue = Array.isArray(field.value) ? field.value : [];

          return (
            <>
              {userExperience?.map((val) => (
                <span key={val.id} className="custom_check me-2">
                  {val.title}
                  <input
                    type="checkbox"
                    checked={currentValue.includes(val.id)}
                    onChange={() =>
                      handleExperienceChange({
                        id: val.id,
                        currentValue,
                        field,
                        setExperience,
                        clearErrors,
                        reg_key,
                      })
                    }
                  />
                  <span className="check_indicator">&nbsp;</span>
                </span>
              ))}

              {/* All of above */}
              <span className="custom_check me-2">
                All of above{" "}
                <input
                  type="checkbox"
                  checked={
                    userExperience?.length > 0 &&
                    currentValue.length === userExperience.length
                  }
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? userExperience.map((exp) => exp.id)
                      : [];

                    field.onChange(newValues);
                    setExperience(newValues);
                    clearErrors(reg_key);
                  }}
                />
                <span className="check_indicator">&nbsp;</span>
              </span>
            </>
          );
        }}
      />

      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors[reg_key].message}
        </span>
      )}
    </Col>
  );
};


export const renderCheckboxField = (field, deps) => {
  const { reg_key } = field;

  switch (reg_key) {
    case "mn_open_to_talk":
    case "mn_loking_for_coach":
    case "lgbtq_discussion":
    case "is_disclaimer":
    case "training_video":
      return renderSimpleCheckbox(field, deps);
    case "mf_mental_health":
      return renderMentalHealthCheckbox(field, deps);
    case "mf_certified_coach":
      return renderCertifiedCoachCheckbox(field, deps);
    case "is_terms":
      return renderTermsCheckbox(field, deps);
    case "is_privacy":
      return renderPrivacyCheckbox(field, deps);
    case "my_consent":
      return renderConsentCheckbox(field, deps);
    case "experience_levels":
      return renderExperienceLevelsCheckbox(field, deps);
    default:
      return null;
  }
};


const validateFromDate = (value, availabilityChecked, toDate) => {
  if (availabilityChecked && !value) {
    return "From date is required when unavailable";
  }

  if (value && toDate && new Date(value) > new Date(toDate)) {
    return "From date must be earlier than To date";
  }

  return undefined;
};

const validateToDate = (value, availabilityChecked, fromDate) => {
  if (availabilityChecked && !value) {
    return "To date is required when unavailable";
  }

  if (value && fromDate && new Date(value) < new Date(fromDate)) {
    return "To date must be later than From date";
  }

  return undefined;
};


// DatePicker field renderers
const renderAvailabilityDatePicker = (field, deps) => {
  const {
    register,
    errors,
    watch,
    setValue,
    trigger,
    availabilityChecked,
    showDatePicker,
    showDateDiv,
    fromDate,
    toDate,
    checkFromDate,
    checkToDate,
  } = deps;
  const { status, label } = field;

  return status ? (
    <>
      {/* Checkbox for availability */}
      <Col md="12" className="">
        <span className="custom_check me-2 mb-2">
          {label}
          <input
            type="checkbox"
            name="availability"
            checked={availabilityChecked}
            {...register("availability")}
            onChange={(e) => {
              showDatePicker(e);
              setValue("availability", e.target.checked);
              trigger(["from_date", "to_date"]);
            }}
          />
          <span className="check_indicator">&nbsp;</span>
        </span>
      </Col>

      {/* From and To Date Fields */}
      <div className="d-flex mb-3 gap-2">
        {/* From Date */}
        <Col md={6} style={{ display: showDateDiv }}>
          <Form.Group controlId="fromDate">
            <Form.Label>From</Form.Label>
            <Form.Control
              type="date"
              name="from_date"
              value={fromDate || ""}
              {...register("from_date", {
                validate: (value) =>
                  validateFromDate(
                    value,
                    availabilityChecked,
                    watch("to_date")
                  ),
              })}
              onChange={(e) => {
                const value = e.target.value;
                checkFromDate(value);
                setValue("from_date", value, { shouldValidate: true });
              }}
            />
            {errors?.from_date && (
              <label className="customeValidationText text-left text-danger">
                {errors.from_date.message}
              </label>
            )}
          </Form.Group>
        </Col>

        {/* To Date */}
        <Col md={6} style={{ display: showDateDiv }}>
          <Form.Group controlId="toDate">
            <Form.Label>To</Form.Label>
            <Form.Control
              type="date"
              name="to_date"
              value={toDate || ""}
              {...register("to_date", {
                validate: (value) =>
                  validateToDate(
                    value,
                    availabilityChecked,
                    watch("from_date")
                  ),
              })}
              onChange={(e) => {
                const value = e.target.value;
                checkToDate(value);
                setValue("to_date", value, { shouldValidate: true });
              }}
            />

            {errors?.to_date && (
              <label className="customeValidationText text-left text-danger">
                {errors.to_date.message}
              </label>
            )}
          </Form.Group>
        </Col>
      </div>
    </>
  ) : null;
};

const renderDOBDatePicker = (field, deps) => {
  const { register, errors, setValue, clearErrors, dob, setDob } = deps;
  const { reg_key, label, validation, status, is_required } = field;
  return status ? (
    <Col md={12} className="mt-3 asAFormField mb-3">
      <Form.Group controlId={reg_key}>
        <Form.Label>{label}</Form.Label>

        {(() => {
          // calculate max date = today - 10 years
          const today = new Date();
          const maxDobDate = new Date(today.setFullYear(today.getFullYear() - 10))
            .toISOString()
            .split("T")[0];

          return (
            <Form.Control
              type="date"
              name={reg_key}
              {...register(reg_key, {
                ...buildValidationRules(validation, is_required),
                validate: (value) => {
                  // not required and empty → valid
                  if (!is_required && !value) {
                    return undefined;
                  }

                  // required but empty → error
                  if (is_required && !value) {
                    return "Please select a date.";
                  }

                  // ensure valid format
                  const isValidDate = /^\d{4}-\d{2}-\d{2}$/.test(value);
                  if (!isValidDate) {
                    return "Please enter a valid date format (YYYY-MM-DD)";
                  }

                  // age check
                  const selectedDate = new Date(value);
                  const minAllowed = new Date();
                  minAllowed.setFullYear(minAllowed.getFullYear() - 10);

                  if (selectedDate > minAllowed) {
                    return "You must be at least 10 years old.";
                  }

                  return undefined; // ✅ valid case
                },
              })}
              value={dob || ""}
              max={maxDobDate} // today - 10 years
              onChange={(e) => {
                const formattedDate = e.target.value;
                setDob(formattedDate);
                setValue("dob", formattedDate);
                clearErrors("dob");
              }}
            />

          );
        })()}

        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

export const renderDatePickerField = (field, deps) => {
  const { reg_key, label, placeholder, validation, status, is_required } = field;
  const { register, control, errors, setValue, watch, trigger, clearErrors, employee } = deps;

  if (!status) return null;

  if (reg_key === "availability") {
    return renderAvailabilityDatePicker(field, deps);
  }

  // Handle employee_start_date and employee_end_date
  if (reg_key === "employee_start_date" || reg_key === "employee_end_date") {
    const currentEmployee = (
      employee ||
      (watch ? watch("employee") : "") ||
      (control?._formValues?.employee) ||
      ""
    )?.toLowerCase();

    // If neither Yes nor No is selected, do not show start date or end date
    if (currentEmployee !== "yes" && currentEmployee !== "no") {
      return null;
    }

    // If currently employed (Yes), hide employee_end_date
    if (reg_key === "employee_end_date" && currentEmployee === "yes") {
      return null;
    }

    const isFieldRequired =
      Number(is_required) === 1 ||
      is_required === true ||
      is_required === "true";

    const cleanLabel = (label || "").replaceAll("*", "").trim();
    const displayLabel = isFieldRequired ? `${cleanLabel} *` : cleanLabel;

    const requiredMessage =
      validation
        ?.find((v) => v.key === "required")
        ?.message?.replaceAll(/<[^>]*>/g, "")
        ?.replaceAll("*", "")
        ?.trim() || `${cleanLabel || "Date"} is required`;

    const validationRules = {
      required:
        isFieldRequired &&
        (currentEmployee === "yes" || currentEmployee === "no") &&
        !(reg_key === "employee_end_date" && currentEmployee === "yes")
          ? requiredMessage
          : false,
      validate: (value) => {
        if (currentEmployee !== "yes" && currentEmployee !== "no") {
          return true;
        }

        if (reg_key === "employee_end_date" && currentEmployee === "yes") {
          return true;
        }

        if (!value) {
          if (isFieldRequired) return requiredMessage;
          return true;
        }

        if (reg_key === "employee_start_date") {
          if (currentEmployee === "no" && watch) {
            const endDate = watch("employee_end_date");
            if (endDate) {
              const startNorm = normalizeMonthYear(value);
              const endNorm = normalizeMonthYear(endDate);
              if (startNorm && endNorm) {
                const startDate = new Date(`${startNorm}-01T00:00:00`);
                const endDateObj = new Date(`${endNorm}-01T00:00:00`);
                if (startDate > endDateObj) {
                  return "Start date must be earlier than or equal to end date";
                }
              }
            }
          }
        }

        if (reg_key === "employee_end_date") {
          if (watch) {
            const startDate = watch("employee_start_date");
            if (startDate) {
              const startNorm = normalizeMonthYear(startDate);
              const endNorm = normalizeMonthYear(value);
              if (startNorm && endNorm) {
                const startDateObj = new Date(`${startNorm}-01T00:00:00`);
                const endDateObj = new Date(`${endNorm}-01T00:00:00`);
                if (startDateObj > endDateObj) {
                  return "End date must be greater than or equal to start date";
                }
              }
            }
          }
        }

        return true;
      },
    };

    return (
      <Col md={6} className="asAFormField mb-3">
        <Form.Group controlId={reg_key}>
          <Form.Label className="form-label">{displayLabel}</Form.Label>

          <div className="w-100 react-datepicker-wrapper-custom position-relative">
            {control ? (
              <Controller
                name={reg_key}
                control={control}
                defaultValue=""
                rules={validationRules}
                render={({ field: { onChange, onBlur, value } }) => (
                  <DatePicker
                    id={reg_key}
                    name={reg_key}
                    selected={
                      value && normalizeMonthYear(value)
                        ? new Date(`${normalizeMonthYear(value)}-01T00:00:00`)
                        : null
                    }
                    onChange={(date) => {
                      let dateStr = "";
                      if (date && !isNaN(date.getTime())) {
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, "0");
                        dateStr = `${year}-${month}`;
                      }
                      onChange(dateStr);
                      if (setValue) setValue(reg_key, dateStr, { shouldValidate: true });
                      if (clearErrors) clearErrors(reg_key);
                      if (trigger) {
                        if (reg_key === "employee_start_date") {
                          trigger("employee_end_date");
                        } else if (reg_key === "employee_end_date") {
                          trigger("employee_start_date");
                        }
                      }
                    }}
                    onBlur={onBlur}
                    dateFormat="MMMM yyyy"
                    showMonthYearPicker
                    autoComplete="off"
                    onKeyDown={(e) => e.preventDefault()}
                    placeholderText={placeholder || "Select Month and Year"}
                    className={`form-control ${errors?.[reg_key] ? "is-invalid" : ""}`}
                  />
                )}
              />
            ) : (
              <DatePicker
                id={reg_key}
                name={reg_key}
                selected={
                  watch && watch(reg_key) && normalizeMonthYear(watch(reg_key))
                    ? new Date(`${normalizeMonthYear(watch(reg_key))}-01T00:00:00`)
                    : null
                }
                onChange={(date) => {
                  let dateStr = "";
                  if (date && !isNaN(date.getTime())) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    dateStr = `${year}-${month}`;
                  }
                  if (setValue) setValue(reg_key, dateStr, { shouldValidate: true });
                  if (clearErrors) clearErrors(reg_key);
                  if (trigger) {
                    if (reg_key === "employee_start_date") {
                      trigger("employee_end_date");
                    } else if (reg_key === "employee_end_date") {
                      trigger("employee_start_date");
                    }
                  }
                }}
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                autoComplete="off"
                onKeyDown={(e) => e.preventDefault()}
                placeholderText={placeholder || "Select Month and Year"}
                className={`form-control ${errors?.[reg_key] ? "is-invalid" : ""}`}
              />
            )}
            <FaRegCalendarAlt
              className="position-absolute"
              style={{
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            />
          </div>

          {errors?.[reg_key] && (
            <span
              role="alert"
              style={{
                color: "#dc3545",
                fontSize: "0.875em",
                margin: "4px 0px 8px",
                display: "block",
              }}
            >
              {errors?.[reg_key]?.message}
            </span>
          )}
        </Form.Group>
      </Col>
    );
  }

  if (reg_key === "dob") {
    return renderDOBDatePicker(field, deps);
  }

  // General Date Field
  const isGeneralRequired =
    Number(is_required) === 1 ||
    is_required === true ||
    is_required === "true";

  const cleanGeneralLabel = (label || "").replaceAll("*", "").trim();
  const displayGeneralLabel = isGeneralRequired ? `${cleanGeneralLabel}*` : cleanGeneralLabel;

  const generalRequiredMsg =
    validation
      ?.find((v) => v.key === "required")
      ?.message?.replaceAll(/<[^>]*>/g, "")
      ?.replaceAll("*", "")
      ?.trim() || `${cleanGeneralLabel || "Date"} is required`;

  return (
    <Col md={12} className="asAFormField mb-3">
      <Form.Group controlId={reg_key}>
        <Form.Label>{displayGeneralLabel}</Form.Label>

        {control ? (
          <Controller
            name={reg_key}
            control={control}
            defaultValue=""
            rules={{
              ...buildValidationRules(validation, is_required),
              required: isGeneralRequired ? generalRequiredMsg : false,
            }}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <Form.Control
                type="date"
                id={reg_key}
                ref={ref}
                value={value || ""}
                onChange={(e) => {
                  onChange(e.target.value);
                  if (clearErrors) clearErrors(reg_key);
                }}
                onBlur={onBlur}
              />
            )}
          />
        ) : (
          <Form.Control
            type="date"
            name={reg_key}
            id={reg_key}
            {...register(reg_key, {
              ...buildValidationRules(validation, is_required),
              required: isGeneralRequired ? generalRequiredMsg : false,
            })}
            onChange={(e) => {
              if (setValue) setValue(reg_key, e.target.value, { shouldValidate: true });
              if (clearErrors) clearErrors(reg_key);
            }}
          />
        )}

        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{ color: "#dc3545", fontSize: "0.875em", marginTop: "4px", display: "block" }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  );
};

// File field renderers
const renderImageField = (field, deps) => {
  const { control, errors, selectedFile, src, alt, onChangePicture } = deps;
  const { reg_key, validation, status, is_required } = field;
  return status ? (
    <Col md={12} className="asAFormField mb-3">
      <div className="userprofile text-center">
        <label className="customeValidationImageText text-center form-label" htmlFor={`image-${reg_key}`}>
          Image size should be less than 30 MB*
        </label>
        <span>
          <img
            className="img-fluid rounded-circle userprofile-img"
            src={src}
            alt={alt}
          />
        </span>
        <div>
          <span className="inputfileouter">
            Upload image{" "}
            <Controller
              name={reg_key}
              control={control}
              rules={{
                ...(selectedFile === "" || typeof selectedFile !== "string"
                  ? buildValidationRules(validation, is_required, reg_key)
                  : {}),
              }}
              render={({ field: { onChange, value, ...field } }) => (
                <input
                  {...field}
                  type="file"
                  accept=".png, .jpg, .jpeg"
                  id={`image-${reg_key}`}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    onChange(file); // send single File to react-hook-form
                    onChangePicture(e);
                  }}
                  className="form-control"
                />
              )}
            />
          </span>
        </div>
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </div>
    </Col>
  ) : null;
};

const renderResumeField = (field, deps) => {
  const { control, errors, clearErrors, resumeText, onChangeResume, selectedResume } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;

  if (!status) return null;

  return (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <div className="form-group inputfileupload studentformdashboard mb-0">
        <input
          type="text"
          disabled
          className="form-control uploadInput"
          value={resumeText || ""}
          placeholder={placeholder}
          readOnly
        />

        <span className="uploadfile uploadfile1" style={{ top: "8px" }}>
          <Controller
            name={reg_key}
            control={control}
            rules={
              selectedResume
                ? {}
                : buildValidationRules(validation, is_required, reg_key)
            }
            render={({ field: { onChange, value, ...field } }) => (
              <input
                {...field}
                type="file"
                accept=".pdf"
                onChange={(e) => {
                  onChange(e.target.files);
                  onChangeResume(e);
                  clearErrors(reg_key);
                }}
                title=""
              />
            )}
          />
          Upload
        </span>

        <sub className="ps-2">Upload only PDF format.</sub>

        <div className="ps-2">
          {errors?.[reg_key] && (
            <span
              role="alert"
              style={{
                color: "#dc3545",
                fontSize: "0.875em",
                margin: "4px 0px 8px",
                display: "block",
              }}
            >
              {errors[reg_key].message}
            </span>
          )}
        </div>
      </div>
    </Col>
  );
};

export const renderFileField = (field, deps) => {
  const { reg_key } = field;

  if (reg_key === "image") {
    return renderImageField(field, deps);
  }

  if (reg_key === "resume") {
    return renderResumeField(field, deps);
  }

  return null;
};

const getSubAreaIds = (subcategory = []) =>
  subcategory.map((s) => s.id.toString());


const syncDomainState = ({
  updatedMain,
  updatedSubs,
  setMainArea,
  setSelectedArea,
  setValue,
  field,
  trigger,
}) => {
  setMainArea(updatedMain);
  setSelectedArea(updatedSubs);

  setValue("mainArea", updatedMain);
  setValue("subarea", updatedSubs);
  setValue("domains", updatedMain);

  field.onChange(updatedMain);
  trigger("mainArea");
};


const toggleMainArea = ({
  checked,
  areaData,
  mainArea,
  selectedArea,
  setMainArea,
  setSelectedArea,
  setValue,
  field,
  trigger,
}) => {
  let updatedMain = [...mainArea];
  let updatedSubs = [...selectedArea];
  const areaId = areaData.id.toString();
  const subIds = getSubAreaIds(areaData.subcategory);

  if (checked) {
    updatedMain = [...new Set([...updatedMain, areaId])];
    updatedSubs = [...new Set([...updatedSubs, ...subIds])];
  } else {
    updatedMain = updatedMain.filter((id) => id !== areaId);
    updatedSubs = updatedSubs.filter((id) => !subIds.includes(id));
  }

  syncDomainState({
    updatedMain,
    updatedSubs,
    setMainArea,
    setSelectedArea,
    setValue,
    field,
    trigger,
  });
};
const toggleSubArea = ({
  checked,
  sub,
  areaData,
  mainArea,
  selectedArea,
  setMainArea,
  setSelectedArea,
  setValue,
  field,
  trigger,
}) => {
  let updatedSubs = [...selectedArea];
  let updatedMain = [...mainArea];
  const subId = sub.id.toString();
  const areaId = areaData.id.toString();

  if (checked) {
    updatedSubs.push(subId);
    if (!updatedMain.includes(areaId)) {
      updatedMain.push(areaId);
    }
  } else {
    updatedSubs = updatedSubs.filter((id) => id !== subId);

    const hasAnySub = areaData.subcategory.some((s) =>
      updatedSubs.includes(s.id.toString())
    );

    if (!hasAnySub) {
      updatedMain = updatedMain.filter((id) => id !== areaId);
    }
  }

  syncDomainState({
    updatedMain,
    updatedSubs,
    setMainArea,
    setSelectedArea,
    setValue,
    field,
    trigger,
  });
};

const handleSubAreaCheckboxChange = ({
  event,
  sub,
  areaData,
  mainArea,
  selectedArea,
  setMainArea,
  setSelectedArea,
  setValue,
  field,
  trigger,
}) => {
  toggleSubArea({
    checked: event.target.checked,
    sub,
    areaData,
    mainArea,
    selectedArea,
    setMainArea,
    setSelectedArea,
    setValue,
    field,
    trigger,
  });
};

const createSubAreaChangeHandler = ({
  sub,
  areaData,
  mainArea,
  selectedArea,
  setMainArea,
  setSelectedArea,
  setValue,
  field,
  trigger,
}) => {
  return (event) => {
    handleSubAreaCheckboxChange({
      event,
      sub,
      areaData,
      mainArea,
      selectedArea,
      setMainArea,
      setSelectedArea,
      setValue,
      field,
      trigger,
    });
  };
};

const createMainAreaChangeHandler = ({
  areaData,
  mainArea,
  selectedArea,
  setMainArea,
  setSelectedArea,
  setValue,
  field,
  trigger,
}) => {
  return (event) => {
    toggleMainArea({
      checked: event.target.checked,
      areaData,
      mainArea,
      selectedArea,
      setMainArea,
      setSelectedArea,
      setValue,
      field,
      trigger,
    });
  };
};

// CustomSelect field renderers
const renderDomainsCustomSelect = (field, deps) => {
  const {
    control,
    errors,
    trigger,
    setValue,
    areas,
    mainArea,
    selectedArea,
    setMainArea,
    setSelectedArea,
    roleId,
    setting,
    formState,
  } = deps;
  const { validation, status, is_required } = field;

  if (!status) return null;

  return (
    <div
      className="profilescreen mb-3 my-3"
      style={{ width: "100%" }}
      ref={(el) => {
        if (el) {
          const hasError = !!errors?.mainArea;
          const submitCount = formState?.submitCount || 0;
          const lastSubmitCount = parseInt(el.dataset.lastSubmitCount || "0", 10);

          const isNewError = hasError && !el.dataset.hadError;
          const isNewSubmitAttempt = hasError && submitCount > lastSubmitCount;

          if (isNewError || isNewSubmitAttempt) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
          if (hasError) {
            el.dataset.hadError = "true";
          } else {
            delete el.dataset.hadError;
          }
          el.dataset.lastSubmitCount = submitCount.toString();
        }
      }}
    >
      <h4>Please select up to 3 areas of interest</h4>
      <p className="mt-2">
        {roleId === "Mentor" ? setting?.mentor_domain_text : setting?.mentees_domain_text}
      </p>

      <div className="businessboxouter">
        <Controller
          name="mainArea"
          control={control}
          rules={{
            ...buildValidationRules(validation, is_required),
          }}
          render={({ field }) => (
            <>
              {errors?.mainArea && (
                <span role="alert" className="text-danger d-block mb-2">
                  {errors.mainArea.message}
                </span>
              )}

              {areas?.map((areaData) => {
                const areaId = areaData.id.toString();
                const isMainSelected =
                  (field.value || []).includes(areaId) ||
                  mainArea.includes(areaId);

                return (
                  <div key={areaId}>
                    <h5>
                      <input
                        type="checkbox"
                        checked={isMainSelected}
                        className={`form-check-input ${errors.mainArea ? "is-invalid" : ""
                          }`}
                        onChange={createMainAreaChangeHandler({
                          areaData,
                          mainArea,
                          selectedArea,
                          setMainArea,
                          setSelectedArea,
                          setValue,
                          field,
                          trigger,
                        })}

                      />
                      <div className="selectboxouter">{areaData.name}</div>
                    </h5>

                    <ul className="bussinessSelect">
                      {areaData.subcategory.map((sub) => (
                        <li key={sub.id}>
                          <input
                            type="checkbox"
                            checked={selectedArea.includes(sub.id.toString())}
                            className="form-check-input"
                            onChange={createSubAreaChangeHandler({
                              sub,
                              areaData,
                              mainArea,
                              selectedArea,
                              setMainArea,
                              setSelectedArea,
                              setValue,
                              field,
                              trigger,
                            })}

                          />
                          <div className="selectboxouter">{sub.name}</div>
                        </li>
                      ))}
                    </ul>

                  </div>
                );
              })}
            </>
          )}
        />
      </div>
    </div>
  );
};

const validateObjective = (value) => {
  if (!Array.isArray(value) || value.length === 0) {
    return "Please select at least one objective.";
  }

  if (value.length > 3) {
    return "You can select a maximum of 3 objectives.";
  }

  return undefined;
};
const toggleObjective = ({
  checked,
  item,
  selectedObjective,
  setSelectedObjective,
  setValue,
  field,
  trigger,
}) => {
  const value = item.value.toString();

  const updatedObjectives = checked
    ? [...new Set([...selectedObjective, value])]
    : selectedObjective.filter((val) => val !== value);

  setSelectedObjective(updatedObjectives);
  setValue("objective", updatedObjectives);
  field.onChange(updatedObjectives);
  trigger("objective");
};

const renderObjectiveCustomSelect = (field, deps) => {
  const {
    control,
    errors,
    trigger,
    setValue,
    objective,
    selectedObjective,
    setSelectedObjective,
    setting,
    roleId
  } = deps;
  const { label, validation, status, is_required } = field;

  if (!status) return null;

  return (
    <div className="profilescreen mb-3 my-3">
      <h4>
        {roleId === "Mentor" ? setting?.mentor_objective_text : setting?.mentees_objective_text}
      </h4>

      <div className="businessboxouter">
        <div className="mb-3">
          <label className="form-label">{label}</label>

          <ul className="bussinessSelect">
            <Controller
              name="objective"
              control={control}
              rules={{
                ...buildValidationRules(validation, is_required),
                validate: validateObjective,
              }}
              render={({ field }) => (
                <>
                  {errors?.objective && (
                    <span
                      role="alert"
                      className="text-danger d-block mb-2"
                    >
                      {errors.objective.message}
                    </span>
                  )}

                  {objective?.map((item) => {
                    const value = item.value.toString();
                    const isChecked = selectedObjective.includes(value);

                    return (
                      <li key={item.value}>
                        <input
                          type="checkbox"
                          value={item.value}
                          checked={isChecked}
                          className={`form-check-input ${errors?.objective ? "is-invalid" : ""
                            }`}
                          onChange={(e) =>
                            toggleObjective({
                              checked: e.target.checked,
                              item,
                              selectedObjective,
                              setSelectedObjective,
                              setValue,
                              field,
                              trigger,
                            })
                          }
                        />
                        <div className="selectboxouter">{item.label}</div>
                      </li>
                    );
                  })}
                </>
              )}
            />
          </ul>
        </div>
      </div>
    </div>
  );
};


export const renderCustomSelectField = (field, deps) => {
  const { reg_key } = field;

  if (reg_key === "board_entity_type" || reg_key === "board_entity_type_2") {
    return renderBoardEntityTypeSelect(field, deps);
  }

  if (reg_key === "course_completed") {
    return renderCoursesCompletedCustomSelect(field, deps);
  }

  if (reg_key === "accredited_director") {
    return renderAccreditedDirectorSelect(field, deps);
  }

  if (reg_key === "indicate_board_experience_level") {
    return renderBoardExperienceLevelSelect(field, deps);
  }

  if (reg_key === "domains") {
    return renderDomainsCustomSelect(field, deps);
  }

  if (reg_key === "objective") {
    return renderObjectiveCustomSelect(field, deps);
  }

  return null;
};

// Textarea field renderer
export const renderTextareaField = (field, deps) => {
  const { register, errors, setValue, watch, setText, countWords } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  return status ? (
    <Col md={12} className="mb-3">
      <div className="">
        <Form.Label>{field.label}</Form.Label>
        <Form.Control
          as="textarea"
          rows="5"
          name={reg_key}
          onChange={(e) => {
            setText(e.target.value);
            setValue(reg_key, e.target.value);
          }}
          placeholder={placeholder}
          className={"form-control"}
          {...register(reg_key, {
            ...buildValidationRules(validation, is_required),
            validate: {
              whiteSpace: (value) =>
                !value || (typeof value === "string" && value.trim().length > 0)
                  ? undefined
                  : "White spaces not allowed.",
            },
          })}
        />
        <p className="mt-2">
          Characters count: {countWords(watch(reg_key) || "")}/
          {reg_key === "about" ? "1500" : "500"}
        </p>

        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </div>
    </Col>
  ) : null;
};

// Board Roles Repeater Component - handles dynamic groups of board role entries
// This component is used for rep_company_name, rep_board_title, rep_start_date, rep_end_date fields
const BoardRolesRepeater = ({ allFields, deps }) => {
  const { watch, setValue, trigger, register, setting, formState } = deps;

  // Get the individual field definitions
  const pastBoardRolesField = allFields?.find(f => f.reg_key === 'past_board_roles');
  const repLabelField = allFields?.find(f => f.reg_key === 'rep_label');
  const companyNameField = allFields?.find(f => f.reg_key === 'rep_company_name');
  const boardTitleField = allFields?.find(f => f.reg_key === 'rep_board_title');
  const startDateField = allFields?.find(f => f.reg_key === 'rep_start_date');
  const endDateField = allFields?.find(f => f.reg_key === 'rep_end_date');

  // Parse board_roles_validation from Redux settings
  // Extract min and max values, ignoring the type field
  // Example: [{"key":"min","type":"Mentor","value":1,"message":"At least 2 board roles required"},{"key":"max","type":"Mentees","value":3}]
  // We only care about key, value, and message - ignore type completely
  let minRoles = 0;
  let maxRoles = 6;
  let minValidationMessage = "";

  if (setting?.board_roles_validation) {
    try {
      // Parse JSON string if needed
      const validationData = typeof setting.board_roles_validation === 'string'
        ? JSON.parse(setting.board_roles_validation)
        : setting.board_roles_validation;

      // Find min and max values - ignore the type field completely
      if (Array.isArray(validationData)) {
        // Find the min entry (ignore type)
        const minObj = validationData.find(v => v.key === 'min');
        // Find the max entry (ignore type)
        const maxObj = validationData.find(v => v.key === 'max');

        minRoles = minObj?.value || 0;
        maxRoles = maxObj?.value || 6;
        minValidationMessage = minObj?.message || "";
      }
    } catch (error) {
      console.error('Error parsing board_roles_validation:', error);
    }
  }

  // Prioritize past_board_roles_count from settings if available
  if (
    setting?.past_board_roles_count !== undefined &&
    setting?.past_board_roles_count !== null &&
    setting?.past_board_roles_count !== ""
  ) {
    const parsedCount = parseInt(setting.past_board_roles_count, 10);
    if (!isNaN(parsedCount) && parsedCount > 0) {
      maxRoles = parsedCount;
    }
  } else if (maxRoles < 6) {
    // Ensure limit is at least 6 when past_board_roles_count is not provided
    maxRoles = 6;
  }

  // Check if ALL board role fields have status = 1 (enabled)
  // If any field has status = 0, don't show the entire board roles section
  const allFieldsEnabled = companyNameField?.status === 1 &&
    boardTitleField?.status === 1 &&
    startDateField?.status === 1 &&
    endDateField?.status === 1;

  // Show the repeater ONLY if:
  // 1. All 4 fields exist (fields are defined in registration settings)
  // 2. All 4 fields have status=1 (fields are enabled)
  // 3. maxRoles > 0 (board roles are allowed based on validation settings)
  const shouldShowRepeater = companyNameField && boardTitleField && startDateField && endDateField && allFieldsEnabled && maxRoles > 0;

  // Watch the board_roles array to track current count
  let boardRolesArray = watch('board_roles') || [];

  // Initialize minimum required groups IMMEDIATELY if needed
  // Don't wait for useEffect - do it synchronously on first render
  if (shouldShowRepeater && boardRolesArray.length === 0 && minRoles > 0) {
    const initialRoles = Array.from({ length: minRoles }, () => ({
      rep_company_name: "",
      rep_board_title: "",
      rep_start_date: "",
      rep_end_date: "",
    }));
    setValue('board_roles', initialRoles, { shouldValidate: false });
    boardRolesArray = initialRoles; // Update local variable immediately
  }

  // Register board_roles field with validation
  const checkRequired = (field, value) => {
    if (field?.is_required === 1 && (!value || value.trim() === "")) {
      const requiredValidation = field?.validation?.find(v => v.key === "required");
      return requiredValidation?.message || `${field?.label} is required`;
    }
    return null;
  };

  const validateDate = (role) => {
    if (!role?.is_currently_working && role.rep_start_date && role.rep_end_date) {
      const startDate = new Date(role.rep_start_date);
      const endDate = new Date(role.rep_end_date);

      if (startDate > endDate) {
        return "Start date must be earlier than or equal to end date";
      }
    }
    return null;
  };

  const validateRole = (role) => {
    const error =
      checkRequired(companyNameField, role.rep_company_name) ||
      checkRequired(boardTitleField, role.rep_board_title) ||
      checkRequired(startDateField, role.rep_start_date);

    if (error) return error;

    if (!role?.is_currently_working) {
      const endDateError = checkRequired(endDateField, role.rep_end_date);
      if (endDateError) return endDateError;
    }

    return validateDate(role);
  };

  React.useEffect(() => {
    if (!shouldShowRepeater || !register) return;

    // Capture current field definitions in local variables to avoid stale closure
    const _companyNameField = companyNameField;
    const _boardTitleField = boardTitleField;
    const _startDateField = startDateField;
    const _endDateField = endDateField;
    const _minRoles = minRoles;
    const _minValidationMessage = minValidationMessage;

    const _checkRequired = (field, value) => {
      if (field?.is_required === 1 && (!value || value.trim() === "")) {
        const requiredValidation = field?.validation?.find(v => v.key === "required");
        return requiredValidation?.message || `${field?.label} is required`;
      }
      return null;
    };

    const _validateDate = (role) => {
      if (!role?.is_currently_working && role.rep_start_date && role.rep_end_date) {
        const startDate = new Date(role.rep_start_date);
        const endDate = new Date(role.rep_end_date);
        if (startDate > endDate) {
          return "Start date must be earlier than or equal to end date";
        }
      }
      return null;
    };

    const _validateRole = (role) => {
      const error =
        _checkRequired(_companyNameField, role.rep_company_name) ||
        _checkRequired(_boardTitleField, role.rep_board_title) ||
        _checkRequired(_startDateField, role.rep_start_date);

      if (error) return error;

      if (!role?.is_currently_working) {
        const endDateError = _checkRequired(_endDateField, role.rep_end_date);
        if (endDateError) return endDateError;
      }

      return _validateDate(role);
    };

    register("board_roles", {
      validate: (value) => {
        // value may be a JSON string (from hidden input) or the array directly
        let roles;
        try {
          roles = typeof value === "string" ? JSON.parse(value) : (value || []);
        } catch {
          roles = [];
        }

        // If any sub-field is required, at least one role entry is required
        const anySubFieldRequired =
          _companyNameField?.is_required === 1 ||
          _boardTitleField?.is_required === 1 ||
          _startDateField?.is_required === 1 ||
          _endDateField?.is_required === 1;

        if (anySubFieldRequired && roles.length === 0) {
          const requiredMsg =
            _companyNameField?.validation?.find(v => v.key === "required")?.message ||
            "At least one board role is required.";
          return requiredMsg;
        }

        if (_minRoles > 0 && roles.length < _minRoles) {
          return _minValidationMessage || `At least ${_minRoles} board role(s) required`;
        }

        for (const role of roles) {
          const roleError = _validateRole(role);

          if (roleError) return roleError;
        }

        return true;
      }
    });

  }, [
    shouldShowRepeater,
    minRoles,
    minValidationMessage,
    companyNameField,
    boardTitleField,
    startDateField,
    endDateField,
    register,
    setting,
  ]);

  // If any of these fields don't exist, don't render
  if (!companyNameField || !boardTitleField || !startDateField) {
    return null;
  }

  // Check if user can add more roles (current count < max)
  const canAddMore = boardRolesArray.length < maxRoles;

  // Add a new empty board role group
  const handleAddRole = () => {
    if (canAddMore) {
      const newRole = {
        rep_company_name: "",
        rep_board_title: "",
        rep_start_date: "",
        rep_end_date: "",
      };
      const updatedRoles = [...boardRolesArray, newRole];
      setValue('board_roles', updatedRoles);
      // Don't clear errors here - let validation run naturally
    }
  };

  // Remove a specific board role group by index
  const handleRemoveRole = (index) => {
    const updatedRoles = boardRolesArray.filter((_, i) => i !== index);
    setValue('board_roles', updatedRoles);
    trigger('board_roles');
  };

  // Update a specific field within a board role group
  const handleFieldChange = (index, fieldName, value) => {
    const updatedRoles = [...boardRolesArray];
    updatedRoles[index] = {
      ...updatedRoles[index],
      [fieldName]: value,
    };

    // Special handling for "Currently Working" checkbox - user choice, multiple can be checked
    if (fieldName === 'is_currently_working') {
      if (value === true) {
        // Clear the end date for the currently working role
        updatedRoles[index].rep_end_date = "";
      }
    }

    // Special handling for end date field - NO AUTO-CHECK
    // Don't auto-check "Currently Working" when end date is cleared
    // User must explicitly check the checkbox

    setValue('board_roles', updatedRoles);
    // Trigger validation after change to update errors
    trigger('board_roles');
  };

  // Validate individual field based on field definition from API
  const validateField = (index, fieldName, fieldDef) => {
    const role = boardRolesArray[index];
    const value = role?.[fieldName];

    const requiredError = checkRequiredValidation(value, fieldDef);
    if (requiredError) return requiredError;

    const validationError = checkFieldValidations(value, fieldDef);
    if (validationError) return validationError;

    return null;
  };

  const checkRequiredValidation = (value, fieldDef) => {
    if (fieldDef?.is_required !== 1) return null;

    if (!value || value.trim() === "") {
      const requiredValidation = fieldDef?.validation?.find(
        (v) => v.key === "required"
      );
      return requiredValidation?.message || `${fieldDef?.label} is required`;
    }

    return null;
  };

  const checkFieldValidations = (value, fieldDef) => {
    // ✅ VERY IMPORTANT FIX
    if (!value || value.trim() === "") return null;

    if (!fieldDef?.validation) return null;

    for (const validation of fieldDef.validation) {
      const error =
        validateMin(value, validation) ||
        validateMax(value, validation) ||
        validatePattern(value, validation);

      if (error) return error;
    }

    return null;
  };

  const validateMin = (value, validation) => {
    if (validation.key === "minlength" || validation.key === "min") {
      if (value.length < validation.length) {
        return validation.message;
      }
    }
    return null;
  };

  const validateMax = (value, validation) => {
    if (validation.key === "maxlength" || validation.key === "max") {
      if (value.length > validation.length) {
        return validation.message;
      }
    }
    return null;
  };

  const validatePattern = (value, validation) => {
    if (validation.key === "pattern" && validation.regex) {
      const regex = new RegExp(validation.regex.slice(1, -1));
      if (!regex.test(value)) {
        return validation.message;
      }
    }
    return null;
  };

  // Validate all board roles before submission
  // Validate date range: rep_start_date must be <= rep_end_date
  // Start Date is required, End Date is optional (unless not currently working)
  const validateDateRange = (index) => {
    const role = boardRolesArray[index];

    // If currently working, end date is not required and validation is skipped
    if (role?.is_currently_working) {
      return null;
    }

    // If not currently working, validate date range
    if (role?.rep_start_date && role?.rep_end_date) {
      const startDate = new Date(role.rep_start_date);
      const endDate = new Date(role.rep_end_date);

      if (startDate > endDate) {
        return "Start date must be earlier than or equal to end date";
      }
    }

    // If only end date is provided without start date, that's an error
    if (!role?.rep_start_date && role?.rep_end_date) {
      return "Start date is required when end date is provided";
    }

    return null;
  };

  if (!shouldShowRepeater) {
    return null;
  }

  // Only show validation errors if form has been submitted
  const showValidationErrors = formState?.isSubmitted;
  const isBoardRoleRequired =
    companyNameField?.is_required === 1 ||
    boardTitleField?.is_required === 1 ||
    startDateField?.is_required === 1 ||
    endDateField?.is_required === 1;
  const hasEmptyRole = boardRolesArray.every(role =>
    !role.rep_company_name &&
    !role.rep_board_title &&
    !role.rep_start_date &&
    !role.rep_end_date
  );

  return (
    <>
      {/* Hidden input so RHF tracks board_roles in its field map and runs validate on submit */}
      <input
        type="hidden"
        {...register("board_roles")}
        value={JSON.stringify(boardRolesArray)}
        readOnly
      />
      {/* Render each board role group inline */}
      {boardRolesArray.map((role, index) => {

        // Validation errors
        const companyError = showValidationErrors
          ? validateField(index, "rep_company_name", companyNameField)
          : null;

        const titleError = showValidationErrors
          ? validateField(index, "rep_board_title", boardTitleField)
          : null;

        const startDateError = showValidationErrors
          ? validateField(index, "rep_start_date", startDateField)
          : null;

        const dateRangeError = showValidationErrors
          ? validateDateRange(index)
          : null;

        // End date validation logic
        const isEndDateRequired =
          showValidationErrors &&
          !role?.is_currently_working &&
          endDateField?.is_required === 1;

        const isEndDateEmpty =
          !role?.rep_end_date || role?.rep_end_date === "";

        const showEndDateError = isEndDateRequired && isEndDateEmpty;

        const endDateErrorMessage =
          endDateField?.validation?.find(v => v.key === "required")?.message ||
          `${endDateField?.label} is required`;



        return (
          <Col md={12} key={`board_role_${role.id}`} className="mb-4">
            {index === 0 && (
              <>
                <Form.Label>{pastBoardRolesField?.label}</Form.Label>
                {/* Disclaimer */}
                {repLabelField?.status === 1 && repLabelField?.label && (
                  <div className="mx-0 mb-2" style={{ fontSize: "0.875em", color: "#6c757d" }}
                    dangerouslySetInnerHTML={{ __html: repLabelField.label }}
                  />
                )}
                {showValidationErrors && isBoardRoleRequired && hasEmptyRole && (
                  <div style={{ color: "#dc3545", fontSize: "0.875em" }}>
                    Add at least one board role
                  </div>
                )}
              </>
            )}
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                padding: "20px",
                backgroundColor: "#f8f9fa",
              }}
            >
              <div className="row">

                {/* Company Name */}
                <Col md={12} className="mb-3">
                  <Form.Label className="form-label">
                    {companyNameField?.label} {companyNameField?.is_required ? "*" : ""}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    id={`rep_company_name_${index}`}
                    name={`rep_company_name_${index}`}
                    placeholder={companyNameField?.placeholder}
                    maxLength={255}
                    value={role?.rep_company_name || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "rep_company_name", e.target.value)
                    }
                    className={companyError ? "is-invalid" : ""}
                  />
                  {companyError && (
                    <span
                      role="alert"
                      style={{
                        color: "#dc3545",
                        fontSize: "0.875em",
                        margin: "4px 0px 8px",
                        display: "block",
                      }}
                    >
                      {companyError}
                    </span>
                  )}
                </Col>

                {/* Board Title */}
                <Col md={12} className="mb-3">
                  <Form.Label className="form-label">
                    {boardTitleField?.label} {boardTitleField?.is_required ? "*" : ""}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    id={`rep_board_title_${index}`}
                    name={`rep_board_title_${index}`}
                    placeholder={boardTitleField?.placeholder}
                    maxLength={255}
                    value={role?.rep_board_title || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "rep_board_title", e.target.value)
                    }
                    className={titleError ? "is-invalid" : ""}
                  />
                  {titleError && (
                    <span
                      role="alert"
                      style={{
                        color: "#dc3545",
                        fontSize: "0.875em",
                        margin: "4px 0px 8px",
                        display: "block",
                      }}
                    >
                      {titleError}
                    </span>
                  )}
                </Col>

                {/* Start Date */}
                <Col md={6} className="mb-3">
                  <Form.Label className="form-label">
                    {startDateField?.label} {startDateField?.is_required ? "*" : ""}
                  </Form.Label>
                  <div className="w-100 react-datepicker-wrapper-custom position-relative">
                    <DatePicker
                      id={`rep_start_date_${index}`}
                      name={`rep_start_date_${index}`}
                      selected={
                        role?.rep_start_date
                          ? new Date(
                              `${normalizeMonthYear(role.rep_start_date)}-01T00:00:00`
                            )
                          : null
                      }
                      onChange={(date) => {
                        if (date) {
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          handleFieldChange(index, "rep_start_date", `${year}-${month}`);
                        } else {
                          handleFieldChange(index, "rep_start_date", "");
                        }
                      }}
                      dateFormat="MMMM yyyy"
                      showMonthYearPicker
                      autoComplete="off"
                      onKeyDown={(e) => e.preventDefault()}
                      placeholderText={startDateField?.placeholder || "Select Month and Year"}
                      className={`form-control ${startDateError ? "is-invalid" : ""}`}
                    />
                    <FaRegCalendarAlt
                      className="position-absolute"
                      style={{
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                      }}
                    />
                  </div>
                  {startDateError && (
                    <span
                      role="alert"
                      style={{
                        color: "#dc3545",
                        fontSize: "0.875em",
                        margin: "4px 0px 8px",
                        display: "block",
                      }}
                    >
                      {startDateError}
                    </span>
                  )}
                </Col>

                {/* End Date */}
                {!role?.is_currently_working && (
                  <Col md={6} className="mb-3">
                    <Form.Label className="form-label">
                      {endDateField?.label} {endDateField?.is_required ? "*" : ""}
                    </Form.Label>

                    <div className="w-100 react-datepicker-wrapper-custom position-relative">
                      <DatePicker
                        id={`rep_end_date_${index}`}
                        name={`rep_end_date_${index}`}
                        selected={
                          role?.rep_end_date
                            ? new Date(
                                `${normalizeMonthYear(role.rep_end_date)}-01T00:00:00`
                              )
                            : null
                        }
                        onChange={(date) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            handleFieldChange(index, "rep_end_date", `${year}-${month}`);
                          } else {
                            handleFieldChange(index, "rep_end_date", "");
                          }
                        }}
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        autoComplete="off"
                        onKeyDown={(e) => e.preventDefault()}
                        placeholderText={endDateField?.placeholder || "Select Month and Year"}
                        className={`form-control ${showEndDateError ? "is-invalid" : ""}`}
                        disabled={role?.is_currently_working}
                      />
                      <FaRegCalendarAlt
                        className="position-absolute"
                        style={{
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                        }}
                      />
                    </div>

                    {showEndDateError && (
                      <span
                        role="alert"
                        style={{
                          color: "#dc3545",
                          fontSize: "0.875em",
                          margin: "4px 0px 8px",
                          display: "block",
                        }}
                      >
                        {endDateErrorMessage}
                      </span>
                    )}

                    {dateRangeError && (
                      <span
                        style={{
                          color: "#dc3545",
                          fontSize: "0.875em",
                          margin: "4px 0px 8px",
                          display: "block",
                        }}
                      >
                        {dateRangeError}
                      </span>
                    )}
                  </Col>
                )}

                {/* Checkbox + Remove */}
                <Col md={12}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Form.Check
                      type="checkbox"
                      id={`currently_working_${index}`}
                      label="Currently in this role"
                      checked={role?.is_currently_working || false}
                      onChange={(e) =>
                        handleFieldChange(index, "is_currently_working", e.target.checked)
                      }
                    />

                    {boardRolesArray.length > minRoles && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger d-flex align-items-center justify-content-center"
                        onClick={() => handleRemoveRole(index)}
                        title="Remove this board role"
                        style={{ width: "32px", height: "32px", padding: 0 }}
                      >
                        <FaMinus size={12} />
                      </button>
                    )}
                  </div>
                </Col>

              </div>
            </div>
          </Col>
        );
      })}

      {/* Minimum count validation message - only show if form was submitted */}
      {showValidationErrors && minRoles > 0 && boardRolesArray.length > 0 && boardRolesArray.length < minRoles && (
        <Col md={12} className="mb-3">
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
            }}
          >
            {minValidationMessage || `At least ${minRoles} board role(s) required`}
          </span>
        </Col>
      )}

      {/* Add More Button - shown only if max not reached, placed after last end_date */}
      {/* Show add button if: current count < max AND we have at least min groups */}
      {canAddMore && boardRolesArray.length >= minRoles && (
        <Col md={12} className="mb-3">
          <button
            type="button"
            className="btn loginsubmit btn-red"
            onClick={handleAddRole}
            title={`Add another board role (${boardRolesArray.length}/${maxRoles})`}
          >
            + Add Board Role
          </button>
        </Col>
      )}
    </>
  );
};

BoardRolesRepeater.propTypes = {
  allFields: PropTypes.shape({
    find: PropTypes.func
  }).isRequired,
  deps: PropTypes.shape({
    errors: PropTypes.shape({
      board_roles: PropTypes.string
    }).isRequired,
    watch: PropTypes.func,
    setValue: PropTypes.func,
    trigger: PropTypes.func,
    register: PropTypes.string,
    setting: PropTypes.shape({
      board_roles_validation: PropTypes.string
    }).isRequired,
    formState: PropTypes.shape({
      isSubmitted: PropTypes.string
    }).isRequired,

  }).isRequired
}

export const renderBoardRolesInline = (allFields, deps) => {
  return <BoardRolesRepeater allFields={allFields} deps={deps} />;
};

// Main render field function
export const renderField = (field, deps) => {
  const { field_type } = field;

  switch (field_type) {
    case "text":
      return renderTextField(field, deps);
    case "email":
      return renderEmailField(field, deps);
    case "password":
      return renderPasswordField(field, deps);
    case "SingleSelect":
      return renderSingleSelectField(field, deps);
    case "radio":
      return renderRadioField(field, deps);
    case "checkbox":
      return renderCheckboxField(field, deps);
    case "DatePicker":
      return renderDatePickerField(field, deps);
    case "file":
      return renderFileField(field, deps);
    case "CustomSelect":
      return renderCustomSelectField(field, deps);
    case "textarea":
      return renderTextareaField(field, deps);
    // default:
    //   return field.status ? <div className="mx-3 mt-2 mb-3">{field.label}</div> : "";
    default: {
      // Handle unknown field types as text labels (like disclaimer)
      const { label, status } = field;
      if (field?.reg_key == "past_board_roles") {
        return null;
      }
      else {
        return status ? (
          <div className="mx-3 mt-2 mb-3">{label}</div>
        ) : null;
      }
    }
  }
};